import { CalculosInventario, Inventario } from 'src/drheranca/types';
import { ExcelCellValue, ExcelSheetData } from '../types';
import {
  buildAcordoMatrixRows,
  resolveAcordoPartilhaNames,
} from './acordoMatrix';

function purposeFromExcelBase(
  base: 'DATA_ESCRITURA' | 'DATA_PARTILHA',
): 'escritura' | 'partilha' {
  return base === 'DATA_ESCRITURA' ? 'escritura' : 'partilha';
}

export function buildModoPartilhaExcelSheet(
  inventario: Inventario,
  calculos: CalculosInventario,
): ExcelSheetData | null {
  const isAcordoUsufruto =
    calculos.modoPartilhaAplicado === 'acordo_doacao_usufruto';
  const acordo = calculos.acordoUsufruto;
  if (!isAcordoUsufruto || !acordo) return null;

  const itcmdAliquota = calculos.itcmdAliquota ?? 0.04;
  const fracUsuPct = (acordo.fracaoUsufrutoImovel * 100).toFixed(2);
  const { conjugeNome, herdeiroNome } = resolveAcordoPartilhaNames(calculos);

  const rows: ExcelCellValue[][] = [];
  rows.push([
    'Modo aplicado',
    'Acordo com doação derivada e reserva de usufruto (SP)',
    null,
    null,
  ]);
  rows.push([
    'Descrição',
    `Cônjuge meeiro recebe usufruto vitalício de ${fracUsuPct}% do imóvel + bens integrais; herdeiro recebe nua-propriedade. Excedente da meação ideal configura doação derivada (ITCMD Doação SP 4%).`,
    null,
    null,
  ]);

  const renderMatrixBlock = (
    titulo: string,
    base: 'DATA_ESCRITURA' | 'DATA_PARTILHA',
    totalConjugeAgregado: number,
    totalHerdeiroAgregado: number,
    meacaoIdealAgregada: number,
    doacaoDerivada: number,
  ) => {
    rows.push([null, null, null, null]);
    rows.push([titulo, null, null, null]);
    rows.push([
      'Bem',
      'Valor total',
      `${conjugeNome} (cônjuge meeiro)`,
      `${herdeiroNome} (herdeiro)`,
    ]);

    const matrix = buildAcordoMatrixRows(
      acordo,
      inventario,
      purposeFromExcelBase(base),
    );
    let totalGeral = 0;
    matrix.forEach((r) => {
      rows.push([
        `${r.bem}\n${r.fracaoNota ?? ''}`,
        r.valorTotal > 0.005 ? r.valorTotal : null,
        r.valorConjuge > 0.005 ? r.valorConjuge : null,
        r.valorHerdeiro > 0.005 ? r.valorHerdeiro : null,
      ]);
      totalGeral += r.valorTotal;
    });

    rows.push(['Total', totalGeral, totalConjugeAgregado, totalHerdeiroAgregado]);
    rows.push([
      `Diferença vs meação ideal (referência: ${(meacaoIdealAgregada || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})`,
      null,
      totalConjugeAgregado - meacaoIdealAgregada,
      doacaoDerivada,
    ]);
  };

  renderMatrixBlock(
    'Partilha por bem — valor venal atualizado (base ITCMD Doação SP)',
    'DATA_ESCRITURA',
    acordo.quinhaoRealConjugeDataEscrituraBrl,
    acordo.quinhaoRealHerdeiroDataEscrituraBrl,
    acordo.meacaoIdealConjugeDataEscrituraBrl,
    acordo.doacaoDerivadaDataEscrituraBrl,
  );

  renderMatrixBlock(
    'Partilha por bem — declarado em escritura (IR / partilha)',
    'DATA_PARTILHA',
    acordo.quinhaoRealConjugeDataPartilhaBrl,
    acordo.quinhaoRealHerdeiroDataPartilhaBrl,
    acordo.meacaoIdealConjugeDataPartilhaBrl,
    acordo.doacaoDerivadaDataPartilhaBrl,
  );

  rows.push([null, null, null, null]);
  rows.push([
    'ITCMD Doação derivada (SP)',
    'Doação derivada (valor venal atualizado) × alíquota fixa SP (Lei 10.705/00 art. 16)',
    acordo.doacaoDerivadaDataEscrituraBrl,
    null,
  ]);
  rows.push(['× Alíquota ITCMD Doação SP', null, itcmdAliquota, null]);
  rows.push(['= ITCMD Doação derivada', null, acordo.itcmdDoacaoDerivadaBrl, null]);

  return {
    rows,
    colWidths: [48, 22, 28, 28],
    valueColumnStart: 1,
  };
}
